import { useSelector } from 'react-redux'
import {
  type CustomFieldValidatorState,
  InternalDisplayPreference,
  Utils,
  Validators,
} from '@pbt/pbt-ui-components'

import { getDocumentTypes } from '~/store/reducers/constants'
import { getDocument } from '~/store/reducers/documents'
import type {
  Document,
  DocumentCreate,
  DocumentElementCreate,
  DocumentElementEdit,
} from '~/types'

/**
 * Removes IDs from document elements and their nested options.
 * Used when creating new documents to prevent "detached entity passed to persist" errors.
 */
export const removeIdsFromDocumentElement = (
  element: DocumentElementEdit,
): DocumentElementCreate => {
  const { id: elementId, ...elementWithoutId } = element

  // Check if the element has options (an array) and remove ids from each option
  if (
    'options' in elementWithoutId &&
    Array.isArray(elementWithoutId.options)
  ) {
    // @ts-ignore
    elementWithoutId.options = elementWithoutId.options.map((option) => ({
      ...option,
      id: undefined,
    }))
  }

  // Check if the element has a single option and remove its id
  if ('option' in elementWithoutId) {
    // @ts-ignore
    elementWithoutId.option = { ...elementWithoutId.option, id: undefined }
  }

  return elementWithoutId
}

/**
 * Removes IDs from all document elements and their nested options in a document.
 * Used when creating new documents to prevent "detached entity passed to persist" errors.
 */
export const removeIdsFromDocument = (document: Document): DocumentCreate => {
  const { id, ...documentWithoutId } = document
  return {
    ...documentWithoutId,
    template: document.template
      ? {
          ...document.template,
          documentElements: document.template.documentElements?.map(
            removeIdsFromDocumentElement,
          ),
        }
      : undefined,
  }
}

export const validateSummarizeInOneLineField = ({
  state: { howToDisplay, summarizeInOneLine },
}: CustomFieldValidatorState) => {
  if (howToDisplay === InternalDisplayPreference.SUMMARIZED) {
    return Validators.notEmptyFormField(summarizeInOneLine)
  }
  return true
}

export const useIsFormTypeDocument = (documentId: string | undefined) => {
  const document = useSelector(getDocument(documentId))
  const DocumentTypes = useSelector(getDocumentTypes)
  const FormType = Utils.findConstantIdByName('Form', DocumentTypes) || {}
  return document?.type === FormType
}
